import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, LogOut, Package, User as UserIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Signed-in dropdown for the top nav. Shows the user's Google picture
// (or a fallback avatar), their display name / email, and quick links
// to /account/orders and sign out. Auto-closes on outside click and
// on route link click.
export default function AccountMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;

  const displayName = user.name || user.email?.split("@")[0] || "Account";
  const picture = user.picture || null;
  const initials = displayName
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div ref={wrapRef} className="relative hidden sm:block" data-testid="account-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full border border-line bg-white px-2 py-1 text-sm text-ink transition-colors hover:border-brandblue"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-menu-trigger"
      >
        {picture ? (
          <img
            src={picture}
            alt=""
            className="h-6 w-6 rounded-full object-cover"
            draggable={false}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            data-testid="account-menu-avatar"
          />
        ) : (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brandblue/10 text-[10px] font-bold text-brandblue">
            {initials || <UserIcon className="h-3 w-3" />}
          </span>
        )}
        <span className="max-w-[120px] truncate font-medium" data-testid="account-menu-name">
          {displayName.split(" ")[0]}
        </span>
        <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl panel-warm bg-white shadow-lg"
          data-testid="account-menu-dropdown"
        >
          <div className="border-b border-line px-4 py-3">
            <p className="truncate font-heading text-sm font-bold text-ink">{displayName}</p>
            <p className="mt-0.5 truncate font-mono text-[11px] text-subtle" data-testid="account-menu-email">
              {user.email}
            </p>
          </div>
          <Link
            to="/account/orders"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-brandbluelight"
            data-testid="account-menu-orders"
          >
            <Package className="h-4 w-4 text-brandblue" strokeWidth={1.5} />
            Order history
          </Link>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="flex w-full items-center gap-2 border-t border-line px-4 py-3 text-sm text-ink transition-colors hover:bg-brandbluelight"
            data-testid="account-menu-signout"
          >
            <LogOut className="h-4 w-4 text-subtle" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
